import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit, Loader2, X, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ManageProducts = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);

    // Form State
    const [name, setName] = useState('');
    const [catId, setCatId] = useState('');
    const [keyFeat, setKeyFeat] = useState('');
    const [img1, setImg1] = useState(null);
    const [img2, setImg2] = useState(null);
    const [subFeatures, setSubFeatures] = useState([{ title: '', description: '' }]);
    const [mainFeatures, setMainFeatures] = useState([{ title: '', description: '' }]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const token = localStorage.getItem('adminToken');
        const headers = { authorization: `Bearer ${token}` };
        try {
            const [cRes, pRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewcategories`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewproducts`, { headers })
            ]);
            setCategories(cRes.data.categories);
            setProducts(pRes.data.products);
        } catch (err) { 
            console.error("Fetch error:", err); 
        } finally {
            setFetchLoading(false);
        }
    };

    const handleArrayChange = (setter, list, index, field, value) => {
        const updated = [...list];
        updated[index][field] = value;
        setter(updated);
    };

    const deleteProduct = async (id) => {
        if (!window.confirm("Delete this product and its images forever?")) return;
        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/deleteproduct/${id}`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setProducts(products.filter(p => p._id !== id));
            alert("Product Deleted");
        } catch (err) {
            alert("Delete failed: " + err.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const formData = new FormData();
        formData.append('productName', name);
        formData.append('categoryId', catId);
        formData.append('keyFeatures', keyFeat);
        formData.append('productImage1', img1);
        formData.append('productImage2', img2);
        formData.append('subFeatures', JSON.stringify(subFeatures));
        formData.append('mainFeatures', JSON.stringify(mainFeatures));

        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/insertproduct`, formData, {
                headers: { 
                    'Content-Type': 'multipart/form-data', 
                    authorization: `Bearer ${localStorage.getItem('adminToken')}` 
                }
            });
            alert("Product Added!");
            // Reset form
            setName(''); setCatId(''); setKeyFeat(''); setImg1(null); setImg2(null);
            setSubFeatures([{ title: '', description: '' }]);
            setMainFeatures([{ title: '', description: '' }]);
            fetchData(); 
        } catch (err) { alert(err.message); }
        setLoading(false);
    };

    if (fetchLoading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" /></div>;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-10">
            <header className="flex justify-between items-center">
                <h1 className="text-3xl font-black uppercase italic">Manage <span className="text-orange-500">Fleet</span></h1>
            </header>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Model Name</label>
                        <input type="text" value={name} onChange={(e)=>setName(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold" required />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Category</label>
                        <select value={catId} onChange={(e)=>setCatId(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold" required>
                            <option value="">Select Category</option>
                            {categories.map(c => <option key={c._id} value={c._id}>{c.categoryName}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Key Features (Summary)</label>
                        <input type="text" value={keyFeat} onChange={(e)=>setKeyFeat(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold" required />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        <label className="text-[10px] font-black uppercase text-slate-400">Image 1 (Main View)</label>
                        <input type="file" onChange={(e)=>setImg1(e.target.files[0])} className="w-full p-3 bg-slate-50 rounded-2xl file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-orange-50 file:text-orange-700 cursor-pointer"  required />
                    </div>
                    <div className="p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        <label className="text-[10px] font-black uppercase text-slate-400">Image 2 (Alt View)</label>
                        <input type="file" onChange={(e)=>setImg2(e.target.files[0])} className="w-full p-3 bg-slate-50 rounded-2xl file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-orange-50 file:text-orange-700 cursor-pointer"  required />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <FeatureSection title="Technical Specs (Sub)" list={subFeatures} setter={setSubFeatures} handler={handleArrayChange} />
                    <FeatureSection title="Core Advantages (Main)" list={mainFeatures} setter={setMainFeatures} handler={handleArrayChange} />
                </div>

                <button type="submit" disabled={loading} className="w-full py-4 bg-orange-500 text-white font-black uppercase rounded-2xl hover:bg-slate-900 transition-all flex justify-center items-center gap-2">
                    {loading ? <Loader2 className="animate-spin" /> : "Deploy Product to Catalog"}
                </button>
            </form>

            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-900 text-white text-[10px] uppercase">
                        <tr>
                            <th className="p-5">Visuals</th>
                            <th className="p-5">Product Name</th>
                            <th className="p-5">Category</th>
                            <th className="p-5 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {products.map((p) => (
                            <tr key={p._id} className="hover:bg-slate-50/50">
                                <td className="p-5 flex gap-2">
                                    <img src={p.productImage1} className="w-12 h-10 object-cover rounded-lg border shadow-sm" alt="img1" />
                                    <img src={p.productImage2} className="w-12 h-10 object-cover rounded-lg border shadow-sm" alt="img2" />
                                </td>
                                <td className="p-5">
                                    <p className="font-black uppercase text-slate-900">{p.productName}</p>
                                </td>
                                <td className="p-5">
                                    {/* p.categoryId is an object because of .populate('categoryId') */}
                                    <span className="text-[10px] font-black px-3 py-1 bg-slate-100 rounded-full text-slate-500 uppercase">
                                        {p.categoryId?.categoryName || "Uncategorized"}
                                    </span>
                                </td>
                                <td className="p-5">
                                    <div className="flex justify-center gap-2">
                                        <button 
                                            onClick={() => navigate(`/admin/update-product/${p._id}`)}
                                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all">
                                            <Edit size={16}/>
                                        </button>
                                        <button 
                                            onClick={() => deleteProduct(p._id)}
                                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all">
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const FeatureSection = ({ title, list, setter, handler }) => (
    <div className="space-y-3 bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
        <div className="flex justify-between items-center">
            <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{title}</h3>
            <button type="button" onClick={() => setter([...list, { title: '', description: '' }])} className="bg-slate-900 text-white p-1 rounded-md hover:bg-orange-500 transition-colors">
                <Plus size={14}/>
            </button>
        </div>
        {list.map((item, idx) => (
            <div key={idx} className="flex gap-2 group">
                <input placeholder="Title" value={item.title} onChange={(e) => handler(setter, list, idx, 'title', e.target.value)} className="w-1/3 p-2 text-xs bg-white border border-slate-100 rounded-lg focus:ring-1 focus:ring-orange-500 outline-none" />
                <input placeholder="Description" value={item.description} onChange={(e) => handler(setter, list, idx, 'description', e.target.value)} className="w-2/3 p-2 text-xs bg-white border border-slate-100 rounded-lg focus:ring-1 focus:ring-orange-500 outline-none" />
                {list.length > 1 && (
                    <button type="button" onClick={() => setter(list.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                        <X size={14}/>
                    </button>
                )}
            </div>
        ))}
    </div>
);

export default ManageProducts;